"use client";

import { useMatchingLocaleContext } from "../../helpers/LocaleStringContext";
import { getIndiaLegalCopy } from "../../helpers/getIndiaLegalCopy";
import { HighInSugarIconCl } from "./icons/HighInSugarIconCl";
import { HighInSugarIconPe } from "./icons/HighInSugarIconPe";
import { VegetarianMarkIn } from "./icons/VegetarianMarkIn";
import { CosmosText, CosmosTextProps } from "@cosmos/web-react";
import classNames from "classnames/bind";
import { ReactNode } from "react";
import styles from "./DietaryMarkList.module.css";

const cx = classNames.bind(styles);

export const DietaryMarkList = ({
  className,
  productIsVegetarian,
  productIsHighSugar,
  appearance = "dark",
  children,
}: {
  className?: string;
  productIsVegetarian?: boolean;
  productIsHighSugar?: boolean;
  appearance?: CosmosTextProps["appearance"];
  children?: ReactNode[];
}) => {
  const matchingLocale = useMatchingLocaleContext();
  const isIndiaMarket = matchingLocale?.market === "in";

  const localeUsesVegetarianMarkIn =
    matchingLocale?.vegetarianMark === "in" && productIsVegetarian;
  const localeUsesHighInSugarMarkCl =
    matchingLocale?.highInSugarMark === "cl" && productIsHighSugar;
  const localeUsesHighInSugarMarkPe =
    matchingLocale?.highInSugarMark === "pe" && productIsHighSugar;

  /* When styling/debugging it's useful to force all marks to appear */
  // const localeUsesVegetarianMark = true;
  // const localeUsesHighInSugarClMark = true;
  // const localeUsesHighInSugarPeMark = true;

  /* Early return if local doesn't show dietary marks and no children provided */
  if (
    !localeUsesVegetarianMarkIn &&
    !localeUsesHighInSugarMarkCl &&
    !localeUsesHighInSugarMarkPe &&
    (!children || children.length === 0) &&
    !isIndiaMarket
  ) {
    return null;
  }

  const dietaryMarksText = getIndiaLegalCopy(
    matchingLocale?.lang ?? "en",
  ).vegetarian;

  /**
   * NOTE: using a div wrapper as it does not have default margins or display that a UL has.
   * Makes it easier to override styles in the parent component.
   */
  return (
    <div className={cx("dietary-marks", className)}>
      <ul className={cx("dietary-marks-list")} role="list">
        {localeUsesVegetarianMarkIn && (
          <li>
            <VegetarianMarkIn className={cx("vegetarian")} />
          </li>
        )}
        {localeUsesHighInSugarMarkCl && (
          <li>
            <HighInSugarIconCl className={cx("high-sugar")} />
          </li>
        )}
        {localeUsesHighInSugarMarkPe && (
          <li>
            <HighInSugarIconPe className={cx("high-sugar")} />
          </li>
        )}
        {/* Render additional children if provided on ingredients page */}
        {children &&
          children.map((child, index) => <li key={index}>{child}</li>)}
      </ul>
      {isIndiaMarket && (
        <CosmosText
          size="xx-small@small x-small@large"
          appearance={appearance}
          kind="subtle"
          className={cx("dietary-marks-text")}
        >
          {dietaryMarksText}
        </CosmosText>
      )}
    </div>
  );
};
